/**
 * Reading a `syncMarkets` response, for the two screens that trigger one.
 *
 * The call resolves whether or not it actually delivered anything: a fixture
 * the feed no longer holds comes back in `skipped` with a reason, and
 * `markets` is zero. Treating a resolved promise as a success leaves the
 * operator pressing "Sync markets" against a panel that still says there are
 * none, with nothing on screen to say why — so both callers go through here.
 *
 * Lifted out of `catalogue-page.tsx` when Edit › Fixtures' drill-down needed
 * the same reading (CAE-1550). That page phrases its own message because it
 * syncs a selection; this module owns the part they agree on — what the
 * reasons are called, and whether the result counts as a success.
 */
import type { SyncMarketsResult } from '../../api/catalogue';

/** The feed's reason codes in words an operator can act on. */
export const skippedReasonLabel = (reason: string): string => {
  if (reason.startsWith('no_active_results')) return 'no active results';
  if (reason === 'fetch_failed') return 'feed error';
  return reason.replaceAll('_', ' ');
};

/** The distinct reasons behind a skipped set, joined — not one line per fixture. */
export const summariseSkippedReasons = (skipped: SyncMarketsResult['skipped']): string => {
  const labels = [...new Set(skipped.map((item) => skippedReasonLabel(item.reason)))];
  return labels.join(', ');
};

/**
 * Did the sync deliver anything?
 *
 * Nothing delivered *and* something skipped is the case worth separating: the
 * request succeeded and the fixture is no better off. Nothing delivered with
 * nothing skipped is a feed that simply has no markets right now, which is a
 * true answer rather than a failure.
 */
export const syncDeliveredNothing = (result: SyncMarketsResult): boolean =>
  result.markets === 0 && result.skipped.length > 0;
